import { combineReducers, configureStore, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { persistReducer, persistStore } from "redux-persist";

const browserStorage = {
  async getItem(key: string) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  async setItem(key: string, value: string) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Preferences are optional; a blocked storage API must not stop startup.
    }
  },
  async removeItem(key: string) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Preferences are optional; a blocked storage API must not stop startup.
    }
  },
};

type TableDensity = "comfortable" | "compact";

type PreferencesState = {
  sidebarCollapsed: boolean;
  tableDensity: TableDensity;
};

const initialPreferences: PreferencesState = {
  sidebarCollapsed: false,
  tableDensity: "comfortable",
};

const preferences = createSlice({
  name: "preferences",
  initialState: initialPreferences,
  reducers: {
    setSidebarCollapsed(state, action: PayloadAction<boolean>) {
      state.sidebarCollapsed = action.payload;
    },
    setTableDensity(state, action: PayloadAction<TableDensity>) {
      state.tableDensity = action.payload;
    },
  },
});

const rootReducer = combineReducers({
  preferences: preferences.reducer,
});

const persistedReducer = persistReducer(
  {
    key: "tawzeef-admin-ui",
    storage: browserStorage,
    whitelist: ["preferences"],
    version: 1,
  },
  rootReducer,
);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }),
});

export const persistor = persistStore(store);
export const preferenceActions = preferences.actions;

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
